import { Link, router } from "@inertiajs/react"
import type { DashboardListing, Listing } from "@/types"
import { Badge } from "@/components/ui/badge"
import { OptimizedImage } from "@/components/ui/optimized-image"
import VideoThumbnail from "@/components/dashboard/videos/video-thumbnail"
import { LISTING_TYPE_LABELS, STATUS_VARIANTS } from "./listing-constants"


function ListingMeta({ listing }: { listing: Listing }) {
  return (
    <div className="flex items-center gap-3 text-xs text-warm-grey">
      {listing.bedrooms != null && (
        <span>
          {listing.bedrooms} bed{listing.bedrooms !== 1 ? "s" : ""}
        </span>
      )}
      {listing.bathrooms != null && (
        <span>
          {listing.bathrooms} bath{listing.bathrooms !== 1 ? "s" : ""}
        </span>
      )}
      {listing.image_count > 0 && (
        <span>
          {listing.image_count} photo{listing.image_count !== 1 ? "s" : ""}
        </span>
      )}
    </div>
  )
}

function CreateVideoButton({
  listing,
  compact = false,
}: {
  listing: Listing
  compact?: boolean
}) {
  function handleClick(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    router.post(`/dashboard/listings/${listing.slug}/videos`)
  }

  if (compact) {
    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-1.5 rounded-md border border-dashed border-warm-grey/30 px-2.5 py-1.5 text-[11px] font-medium text-warm-grey transition-colors hover:border-red/40 hover:text-red"
      >
        <svg
          className="size-3"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 4.5v15m7.5-7.5h-15"
          />
        </svg>
        Create video
      </button>
    )
  }

  return (
    <button
      onClick={handleClick}
      className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-warm-grey/30 py-3 text-xs font-medium text-warm-grey transition-colors hover:border-red/40 hover:text-red"
    >
      <svg
        className="size-3.5"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 4.5v15m7.5-7.5h-15"
        />
      </svg>
      Create video
    </button>
  )
}

export function ListingCardWithVideos({
  listing,
}: {
  listing: DashboardListing
}) {
  const hasImage = listing.image_urls.length > 0
  const videos = listing.videos

  return (
    <div className="group overflow-hidden rounded-xl border border-stone/40 bg-white transition-colors hover:border-red/30 hover:shadow-sm">
      {/* Listing link — image + details */}
      <Link href={`/dashboard/listings/${listing.slug}`}>
        <div className="aspect-[16/10] w-full overflow-hidden bg-stone/20">
          {hasImage ? (
            <OptimizedImage
              src={listing.image_urls[0]}
              alt={listing.address}
              className="size-full object-cover transition-transform group-hover:scale-105"
            />
          ) : (
            <div className="flex size-full items-center justify-center">
              <svg
                className="size-10 text-warm-grey/40"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"
                />
              </svg>
            </div>
          )}
        </div>

        <div className="p-3 sm:p-4">
          <div className="mb-2 flex items-center gap-1.5 sm:gap-2">
            <Badge
              variant={STATUS_VARIANTS[listing.status]}
              className="text-[10px] capitalize"
            >
              {listing.status}
            </Badge>
            <Badge variant="outline" className="text-[10px]">
              {LISTING_TYPE_LABELS[listing.listing_type]}
            </Badge>
          </div>
          <h3 className="mb-1 text-sm font-medium text-charcoal line-clamp-1 group-hover:text-red">
            {listing.address}
          </h3>
          {listing.price && (
            <p className="text-sm font-semibold text-charcoal">
              {listing.price}
            </p>
          )}
          <div className="mt-2">
            <ListingMeta listing={listing} />
          </div>
        </div>
      </Link>

      {/* Videos section */}
      <div className="border-t border-stone/30 bg-cream/30 px-3 py-2.5 sm:px-4 sm:py-3">
        {videos.length > 0 ? (
          <div className="flex items-center gap-2 overflow-x-auto">
            {videos.map((video) => (
              <VideoThumbnail key={video.id} video={video} size="medium" />
            ))}
          </div>
        ) : (
          <CreateVideoButton listing={listing} />
        )}
      </div>
    </div>
  )
}

export function ListingRowWithVideos({
  listing,
}: {
  listing: DashboardListing
}) {
  const hasImage = listing.image_urls.length > 0
  const videos = listing.videos
  const visibleVideos = videos.slice(0, 3)
  const extraCount = videos.length - 3

  return (
    <div className="group overflow-hidden rounded-xl border border-stone/40 bg-white transition-colors hover:border-red/30 hover:shadow-sm">
      {/* Listing details */}
      <Link
        href={`/dashboard/listings/${listing.slug}`}
        className="flex min-w-0 items-center gap-3 px-3 py-2.5 sm:gap-4 sm:px-4 sm:py-3"
      >
        <div className="size-16 shrink-0 overflow-hidden rounded-lg bg-stone/20 sm:size-20">
          {hasImage ? (
            <OptimizedImage
              src={listing.image_urls[0]}
              alt={listing.address}
              className="size-full object-cover transition-transform group-hover:scale-105"
            />
          ) : (
            <div className="flex size-full items-center justify-center">
              <svg
                className="size-6 text-warm-grey/40"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"
                />
              </svg>
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-medium text-charcoal group-hover:text-red">
            {listing.address}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 sm:gap-2">
            <Badge
              variant={STATUS_VARIANTS[listing.status]}
              className="text-[10px] capitalize"
            >
              {listing.status}
            </Badge>
            <Badge variant="outline" className="text-[10px]">
              {LISTING_TYPE_LABELS[listing.listing_type]}
            </Badge>
            {listing.price && (
              <span className="ml-0.5 text-sm font-semibold text-charcoal sm:ml-1">
                {listing.price}
              </span>
            )}
          </div>
        </div>

        <div className="hidden shrink-0 items-center gap-3 text-xs text-warm-grey md:flex">
          {listing.bedrooms != null && (
            <span>
              {listing.bedrooms} bed{listing.bedrooms !== 1 ? "s" : ""}
            </span>
          )}
          {listing.bathrooms != null && (
            <span>
              {listing.bathrooms} bath{listing.bathrooms !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </Link>

      {/* Videos area */}
      <div className="flex items-center gap-2 border-t border-stone/30 bg-cream/30 px-3 py-2.5 sm:px-4 sm:py-3">
        {visibleVideos.length > 0 ? (
          <>
            {visibleVideos.map((video) => (
              <VideoThumbnail key={video.id} video={video} size="small" />
            ))}
            {extraCount > 0 && (
              <span className="text-[11px] font-medium text-warm-grey">
                +{extraCount}
              </span>
            )}
          </>
        ) : (
          <CreateVideoButton listing={listing} compact />
        )}
      </div>
    </div>
  )
}
